import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { Activity, Clock, Filter, RefreshCw, Timer, UserCheck } from 'lucide-react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { formatDateTime, formatDuration, formatRole } from '../../utils/format';

interface LoginSessionRow {
  _id: string;
  user: string;
  email: string;
  role: string;
  loginAt: string;
  logoutAt: string | null;
  durationSeconds: number;
  ip: string;
  userAgent: string;
  closed: boolean;
}

interface Totals {
  count: number;
  totalSeconds: number;
  avgSeconds: number;
}

interface AppUser {
  _id: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Journal des connexions (sprint « traçabilité apprenants » — 01/10/2026).
 * Route : /admin/connections — données : GET /api/admin/login-logs
 */
const AdminConnections: React.FC = () => {
  const [sessions, setSessions] = useState<LoginSessionRow[]>([]);
  const [totals, setTotals] = useState<Totals>({ count: 0, totalSeconds: 0, avgSeconds: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      const list = response.data?.users || [];
      setUsers(list);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit };
      if (userId) params.userId = userId;
      if (from) params.from = new Date(`${from}T00:00:00`).toISOString();
      if (to) params.to = new Date(`${to}T23:59:59`).toISOString();

      const response = await api.get('/admin/login-logs', { params });
      setSessions(response.data?.sessions || []);
      setTotal(response.data?.total || 0);
      setTotals(response.data?.totals || { count: 0, totalSeconds: 0, avgSeconds: 0 });
    } catch (error) {
      console.error('Error loading login logs:', error);
      toast.error('Erreur lors du chargement du journal des connexions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, userId, from, to]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const resetFilters = () => {
    setUserId('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Journal des connexions</h1>
            <p className="text-sm text-gray-500 mt-1">
              Qui se connecte, quand, et combien de temps — {total} session(s) enregistrée(s)
            </p>
          </div>
          <button
            onClick={loadSessions}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraîchir
          </button>
        </div>

        {/* Totaux */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Activity className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Connexions</p>
                <p className="text-2xl font-bold text-gray-900">{totals.count}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Timer className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Temps total</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(totals.totalSeconds)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Clock className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Durée moyenne</p>
                <p className="text-2xl font-bold text-gray-900">{formatDuration(totals.avgSeconds)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Filter className="h-4 w-4 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Filtres</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur</label>
              <select
                value={userId}
                onChange={(e) => { setUserId(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name || user.email} ({formatRole(user.role)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input
                type="date"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input
                type="date"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune connexion enregistrée</h3>
              <p className="text-gray-500">
                Les connexions apparaîtront ici dès le prochain login (elles sont enregistrées automatiquement).
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Début</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session) => (
                    <tr key={session._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{session.email || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatRole(session.role)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDateTime(session.loginAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {session.closed && session.logoutAt ? formatDateTime(session.logoutAt) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800">
                            en cours
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {session.closed ? formatDuration(session.durationSeconds) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" title={session.userAgent}>
                        {session.ip || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <span className="text-sm text-gray-600">
                Page {page} / {totalPages}
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminConnections;
